import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAPI } from '../contexts/APIContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProfilePictureModal = ({ isOpen, onClose, onSelect, currentAvatarUrl }) => {
  const { apiClient } = useAPI();
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState('upload'); // 'upload', 'characters', 'draw'
  const [characters, setCharacters] = useState([]);
  const [loadingCharacters, setLoadingCharacters] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const fileInputRef = useRef(null);

  // Drawing canvas state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(5);
  const [brushColor, setBrushColor] = useState('#000000');
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatedDrawing, setAnimatedDrawing] = useState(null);

  const loadCharacters = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoadingCharacters(true);
      const response = await apiClient.getUserCharacters(user.id);
      if (response?.status && response?.data) {
        setCharacters(response.data);
      }
    } catch (error) {
      console.error('Error loading characters:', error);
      toast.error('Failed to load characters');
    } finally {
      setLoadingCharacters(false);
    }
  }, [user?.id, apiClient]);

  useEffect(() => {
    if (isOpen && selectedTab === 'characters') {
      loadCharacters();
    }
  }, [isOpen, selectedTab, loadCharacters]);

  useEffect(() => {
    if (isOpen && selectedTab === 'draw' && !animatedDrawing) {
      // Initialize canvas when on draw tab and not showing animated result
      setTimeout(() => {
        initCanvas();
      }, 0);
    }
  }, [isOpen, selectedTab, animatedDrawing]);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setAnimatedDrawing(null);
      setUploadedImage(null);
      setSelectedCharacter(null);
    }
  }, [isOpen]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCharacterSelect = (character) => {
    setSelectedCharacter(character);
  };

  // Canvas drawing functions
  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Calculate the scale between canvas internal size and displayed size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Get mouse position relative to canvas and scale to internal coordinates
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    return { x, y };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const { x, y } = getCanvasCoordinates(e);

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const { x, y } = getCanvasCoordinates(e);

    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleAnimateDrawing = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      setIsAnimating(true);

      // Convert canvas to blob
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      // Animate using DALL-E
      const response = await apiClient.animateProfilePicture(blob);
      console.log('Animate profile picture response:', response);

      // Backend returns image_url, not animated_url
      if (response?.status && response?.data?.image_url) {
        setAnimatedDrawing(response.data.image_url);
        toast.success('Drawing animated! Review it below.');
      } else if (response?.image_url) {
        // Handle direct URL response at root level
        setAnimatedDrawing(response.image_url);
        toast.success('Drawing animated! Review it below.');
      } else {
        console.error('Unexpected response structure:', response);
        throw new Error('Animation failed - unexpected response structure');
      }
    } catch (error) {
      console.error('Error animating drawing:', error);
      toast.error('Failed to animate drawing');
    } finally {
      setIsAnimating(false);
    }
  };

  const handleTryAgain = () => {
    setAnimatedDrawing(null);
    // Canvas will be re-initialized by useEffect when it's rendered again
  };

  const handleConfirmAnimated = () => {
    if (animatedDrawing) {
      onSelect(animatedDrawing);
      onClose();
    }
  };

  const handleConfirm = () => {
    if (selectedTab === 'upload' && uploadedImage) {
      onSelect(uploadedImage);
      onClose();
    } else if (selectedTab === 'characters' && selectedCharacter) {
      onSelect(selectedCharacter.animated_image_url || selectedCharacter.handdrawn_image_url);
      onClose();
    } else if (selectedTab === 'draw' && animatedDrawing) {
      handleConfirmAnimated();
    } else {
      toast.error('Please select or draw a picture');
    }
  };

  if (!isOpen) return null;

  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
  const TABS = [
    { id: 'upload', label: 'Upload' },
    { id: 'characters', label: 'My Characters' },
    { id: 'draw', label: 'Draw' },
  ];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)", fontFamily: "'Inter', 'Fredoka', sans-serif" }}
    >
      <div
        className="relative max-w-5xl w-full max-h-[95vh] flex flex-col overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
          border: "2px solid rgba(11,26,51,0.06)",
          borderRadius: 28,
          boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
        }}
      >
        {/* Header */}
        <div className="p-6 pb-4 flex-shrink-0" style={{ borderBottom: "2px solid rgba(11,26,51,0.06)" }}>
          <div className="flex items-start justify-between mb-4 gap-3">
            <div>
              <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                Profile Picture
              </span>
              <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 24, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 0" }}>
                Choose Profile Picture
              </h2>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="transition active:scale-[0.94]"
              style={{
                width: 36, height: 36,
                background: "#FFFFFF",
                color: "#5A6B85",
                border: "2px solid rgba(11,26,51,0.08)",
                borderRadius: 12,
                boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 flex-wrap">
            {TABS.map((tab) => {
              const isActive = selectedTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className="transition-all active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: isActive ? "#0065F8" : "#FFFFFF",
                    color: isActive ? "#FFFFFF" : "#5A6B85",
                    fontWeight: 700,
                    fontSize: 13,
                    border: isActive ? "none" : "2px solid rgba(11,26,51,0.08)",
                    borderRadius: 999,
                    padding: "8px 16px",
                    boxShadow: isActive ? "0px 3px 0px #073B8E" : "0px 2px 0px rgba(11,26,51,0.05)",
                    cursor: "pointer",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Upload Tab */}
          {selectedTab === 'upload' && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="text-center cursor-pointer transition-all"
                style={{
                  background: "#FFFFFF",
                  border: "3px dashed rgba(0,101,248,0.30)",
                  borderRadius: 20,
                  padding: "44px 28px",
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.background = "rgba(0,101,248,0.04)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(0,101,248,0.30)"; e.currentTarget.style.background = "#FFFFFF"; }}
              >
                {uploadedImage ? (
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    className="mx-auto object-cover"
                    style={{
                      width: 128,
                      height: 128,
                      borderRadius: "50%",
                      border: "4px solid #FFFFFF",
                      boxShadow: "0px 4px 0px rgba(11,26,51,0.08), 0px 0px 0px 4px rgba(0,101,248,0.18)",
                    }}
                  />
                ) : (
                  <div>
                    <div
                      className="mx-auto mb-4 flex items-center justify-center"
                      style={{
                        width: 64, height: 64,
                        borderRadius: "50%",
                        background: "rgba(0,101,248,0.10)",
                      }}
                    >
                      <svg width="32" height="32" fill="none" stroke="#0065F8" strokeWidth="2.4" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 16, margin: "0 0 4px" }}>Click to upload an image</p>
                    <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: 0 }}>PNG, JPG, or GIF up to 10MB</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Characters Tab */}
          {selectedTab === 'characters' && (
            <div>
              {loadingCharacters ? (
                <div className="flex items-center justify-center py-12">
                  <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid rgba(0,101,248,0.20)", borderTopColor: "#0065F8", animation: "pp-spin 1s linear infinite" }} />
                  <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, marginLeft: 12, fontWeight: 600 }}>Loading characters…</span>
                </div>
              ) : characters.length === 0 ? (
                <div
                  className="text-center mx-auto"
                  style={{
                    background: "#FFFFFF",
                    border: "2px solid rgba(11,26,51,0.08)",
                    borderRadius: 20,
                    padding: "40px 32px",
                    maxWidth: 420,
                    boxShadow: "0px 3px 0px rgba(11,26,51,0.05)",
                  }}
                >
                  <div
                    className="mx-auto mb-4 flex items-center justify-center"
                    style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(0,101,248,0.10)" }}
                  >
                    <svg width="28" height="28" fill="none" stroke="#0065F8" strokeWidth="2.2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 14a4 4 0 10-8 0M20 21v-1a6 6 0 00-6-6h-4a6 6 0 00-6 6v1M12 11a4 4 0 100-8 4 4 0 000 8z" />
                    </svg>
                  </div>
                  <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 18, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "0 0 6px" }}>
                    No characters yet
                  </h3>
                  <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: 0 }}>
                    Create a character first to use it as your profile picture.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                  {characters.map((character) => {
                    const isSel = selectedCharacter?.id === character.id;
                    return (
                      <button
                        key={character.id}
                        onClick={() => handleCharacterSelect(character)}
                        className="relative overflow-hidden transition-all active:scale-[0.97]"
                        style={{
                          background: "#FFFFFF",
                          border: isSel ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                          borderRadius: 18,
                          boxShadow: isSel
                            ? "0px 5px 0px #073B8E, 0px 14px 28px rgba(0,101,248,0.22)"
                            : "0px 3px 0px rgba(11,26,51,0.06)",
                        }}
                      >
                        <img
                          src={character.animated_image_url || character.handdrawn_image_url}
                          alt={character.name}
                          className="w-full aspect-square object-cover"
                        />
                        <div
                          className="absolute bottom-0 left-0 right-0 p-2 text-center"
                          style={{ background: "linear-gradient(180deg, transparent 0%, rgba(11,26,51,0.78) 100%)" }}
                        >
                          <p className="truncate" style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 13, margin: 0 }}>{character.name}</p>
                        </div>
                        {isSel && (
                          <div
                            className="absolute"
                            style={{
                              top: 8, right: 8,
                              width: 26, height: 26,
                              borderRadius: "50%",
                              background: "#0065F8",
                              color: "#FFFFFF",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0px 2px 0px #073B8E",
                            }}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Draw Tab */}
          {selectedTab === 'draw' && (
            <div className="space-y-4">
              {!animatedDrawing ? (
                <>
                  {/* Drawing Canvas */}
                  <div
                    className="mx-auto"
                    style={{
                      maxWidth: 600,
                      background: "#FFFFFF",
                      border: "3px solid #FFFFFF",
                      borderRadius: 18,
                      boxShadow: "0px 4px 0px rgba(11,26,51,0.08), 0px 12px 28px rgba(11,26,51,0.08)",
                      overflow: "hidden",
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      width={800}
                      height={800}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      style={{ cursor: "crosshair", width: "100%", display: "block", borderRadius: 14 }}
                    />
                  </div>

                  {/* Drawing Controls */}
                  <div
                    className="flex gap-4 items-end flex-wrap"
                    style={{
                      background: "#FFFFFF",
                      border: "2px solid rgba(11,26,51,0.08)",
                      borderRadius: 16,
                      padding: "14px 16px",
                      boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                    }}
                  >
                    <div className="flex-1 min-w-[160px]">
                      <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>
                        Brush Size · <span style={{ color: "#0065F8" }}>{brushSize}px</span>
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="20"
                        value={brushSize}
                        onChange={(e) => setBrushSize(parseInt(e.target.value))}
                        className="w-full pp-range"
                      />
                    </div>
                    <div>
                      <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>
                        Color
                      </label>
                      <input
                        type="color"
                        value={brushColor}
                        onChange={(e) => setBrushColor(e.target.value)}
                        style={{ width: 56, height: 38, borderRadius: 10, border: "2px solid rgba(11,26,51,0.10)", cursor: "pointer", background: "#FFFFFF" }}
                      />
                    </div>
                    <button
                      onClick={clearCanvas}
                      className="transition active:scale-[0.97]"
                      style={{
                        ...FREDOKA,
                        background: "#FFFFFF",
                        color: "#5A6B85",
                        fontWeight: 700,
                        fontSize: 13,
                        border: "2px solid rgba(11,26,51,0.10)",
                        borderRadius: 999,
                        padding: "9px 16px",
                        boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                        cursor: "pointer",
                      }}
                    >
                      Clear
                    </button>
                  </div>

                  {/* Animate Button */}
                  <button
                    onClick={handleAnimateDrawing}
                    disabled={isAnimating}
                    className="w-full transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    style={{
                      ...FREDOKA,
                      background: "#0065F8",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 15,
                      border: "none",
                      borderRadius: 999,
                      padding: "14px 22px",
                      boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                      cursor: isAnimating ? "wait" : "pointer",
                    }}
                  >
                    {isAnimating ? (
                      <>
                        <span style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.30)", borderTopColor: "#FFFFFF", animation: "pp-spin 1s linear infinite" }} />
                        Animating…
                      </>
                    ) : (
                      <>
                        Animate Drawing
                        <span style={{ fontSize: 16 }}>✨</span>
                      </>
                    )}
                  </button>

                  <p className="text-center" style={{ ...FREDOKA, color: "#94A3B8", fontSize: 12 }}>
                    Sketch something and let AI bring it to life.
                  </p>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                      Animated Result
                    </span>
                    <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 16px" }}>
                      Look at that ✨
                    </h3>
                    <div
                      className="mx-auto"
                      style={{
                        maxWidth: 360,
                        background: "#FFFFFF",
                        border: "3px solid #FFFFFF",
                        borderRadius: 20,
                        padding: 12,
                        boxShadow: "0px 4px 0px rgba(11,26,51,0.08), 0px 0px 0px 4px rgba(0,101,248,0.15), 0px 14px 28px rgba(0,101,248,0.18)",
                      }}
                    >
                      <img src={animatedDrawing} alt="Animated drawing" style={{ width: "100%", borderRadius: 12, display: "block" }} />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleTryAgain}
                      className="flex-1 transition active:scale-[0.97]"
                      style={{
                        ...FREDOKA,
                        background: "#FFFFFF",
                        color: "#5A6B85",
                        fontWeight: 700,
                        fontSize: 14,
                        border: "2px solid rgba(11,26,51,0.10)",
                        borderRadius: 999,
                        padding: "12px 22px",
                        boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
                        cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                    <button
                      onClick={handleConfirmAnimated}
                      className="flex-1 transition active:scale-[0.97]"
                      style={{
                        ...FREDOKA,
                        background: "#0065F8",
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: 14,
                        border: "none",
                        borderRadius: 999,
                        padding: "12px 22px",
                        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                        cursor: "pointer",
                      }}
                    >
                      Use This Picture
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="p-5 flex justify-end gap-3 flex-shrink-0"
          style={{ borderTop: "2px solid rgba(11,26,51,0.06)", background: "rgba(245,250,255,0.65)" }}
        >
          <button
            onClick={onClose}
            className="transition active:scale-[0.97]"
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: "#FFFFFF",
              color: "#5A6B85",
              fontWeight: 700,
              fontSize: 14,
              border: "2px solid rgba(11,26,51,0.10)",
              borderRadius: 999,
              padding: "11px 22px",
              boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          {selectedTab !== 'draw' && (
            <button
              onClick={handleConfirm}
              className="transition active:scale-[0.97]"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: "#0065F8",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 14,
                border: "none",
                borderRadius: 999,
                padding: "12px 22px",
                boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                cursor: "pointer",
              }}
            >
              Confirm
            </button>
          )}
        </div>

        <style>{`
          @keyframes pp-spin { to { transform: rotate(360deg); } }
          .pp-range {
            appearance: none;
            -webkit-appearance: none;
            height: 6px;
            background: rgba(11,26,51,0.10);
            border-radius: 999px;
            outline: none;
            cursor: pointer;
          }
          .pp-range::-webkit-slider-thumb {
            appearance: none;
            -webkit-appearance: none;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #0065F8;
            border: 3px solid #FFFFFF;
            box-shadow: 0px 2px 4px rgba(11,26,51,0.20);
          }
          .pp-range::-moz-range-thumb {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #0065F8;
            border: 3px solid #FFFFFF;
            box-shadow: 0px 2px 4px rgba(11,26,51,0.20);
          }
        `}</style>
      </div>
    </div>
  );
};

export default ProfilePictureModal;
